/**
 * V70 타자왕 — 서버 API Interface
 *
 * Phase 2: Mock 구현 (실제 코인 차감/점수 저장 X)
 * Phase 3: 동일 interface 를 Firebase callable 로 교체
 *
 * 사용자 지시:
 *   - 실제 코인 차감이나 공식 점수 저장이 되는 것처럼 표시하지 않는다.
 *   - Phase 3 서버 구현 시 쉽게 연동할 수 있도록 interface 를 분리한다.
 */
import type {
  AcidRainSettings,
  CoinPolicy,
  CoinPolicyChange,
  DailyCoinStatus,
  GameResult,
  LeaderboardRow,
} from './types';
import { DEFAULT_COIN_POLICY, DEFAULT_SETTINGS } from './types';
// V70 Phase 3-A §1·§3: BE 재검증 + 어드민 쓰기 Firestore SSoT
// Phase 3-A (후속): 사용자 조회 getter 도 Firestore 기준으로 갱신 → 어드민 설정 전역 적용
import {
  assertTournamentGameAccess,
  callUpdateTournamentOpenPeriod,
  callUpdateTournamentCoinPolicy,
  callResetTournamentLeaderboard,
  callGetPublicTournamentGameSettings,
  type AccessCheckResp,
} from '../shared/tournamentGameApi';
// V70 Phase 3-C — Feature flag 분기용 (파일 상단 import 유지, 하단에서 사용)
import { isPhase3cServerEnabled } from '../shared/phase3cFlag';
import { serverAcidRainApi } from './server/serverApi';

export interface StartSessionRequest {
  /** 클라 생성 request id — 중복 요청 방지 (Phase 3 BE 가 idempotency 보장) */
  clientRequestId: string;
}

export interface StartSessionResponse {
  sessionId: string;
  /** 결정론적 PRNG seed (서버 서명) */
  seed: string;
  /** 서버 서명된 commitment hash (Phase 3). Phase 2 는 mock */
  commitmentHash: string;
  settings: AcidRainSettings;
  startedAtServerMs: number;
  /**
   * 세션 생성 시점의 리더보드 세대 (epoch) 스냅샷.
   * 세션 진행 중 리더보드가 초기화되어도 이 값은 바뀌지 않으며,
   * `submitScore(req.sessionEpoch)` 로 그대로 전달한다. 조회 시 current epoch 와
   * 다르면 리더보드에 노출되지 않는다 (사용자 지시: 진행 중 세션의 과거 epoch 점수
   * 가 새 리더보드에 유입되지 않도록 보장).
   */
  leaderboardEpoch: number;
}

/**
 * 점수 제출 요청자(mock 범위에서 식별 가능한 최소 shape).
 *
 * Phase 2: FE 가 current auth context 에서 추출해 request 에 포함
 * Phase 3: BE 가 Firebase ID 토큰에서 uid 를 추출 → request.user 는 displayName/
 *          companyCode 참고용으로만 사용, uid 는 서버 신뢰
 */
export interface SubmitScoreUser {
  uid: string;
  displayName: string | null;
  companyCode?: 'KR' | 'CN';
  /** V70: 저장 시점의 소속 조직 ID. 조회 시점에 조직명으로 매핑. */
  orgUnitId?: string;
}

export interface SubmitScoreRequest {
  sessionId: string;
  clientRequestId: string;
  result: GameResult;
  /** StartSession 응답에서 받아둔 세대. 리셋 소급 금지 보장. */
  sessionEpoch: number;
  /** mock 저장 식별용. Phase 3 BE 는 토큰 uid 로 재검증. */
  user: SubmitScoreUser;
  /**
   * Phase 3-C.1 결함 A 수정 — 서버 재계산용 입력 이벤트.
   * - Mock serverApi: 무시 (clientReportedScore 로만 저장)
   * - Server serverApi: appendAcidRainInputEvents chunk 로 서버 전송 후 submitScore 호출
   * - 미지정/빈 배열: 서버가 serverScore=0 재계산 → flagged='low'/'invalid'
   */
  inputEvents?: Array<{
    seq: number;
    type: 'cleared' | 'missed';
    elapsedMs: number;
    word: string;
    combo: number;
  }>;
  /**
   * Phase 3-C.2 보완 — 재시도 취소 토큰.
   * - Mock serverApi: 무시
   * - Server serverApi: chunk flush + submit backoff sleep 중 cancelled=true 세팅 시
   *                     즉시 Error throw (dangling timer 방지).
   * - 호출자가 생성 (createCancelToken) 하여 전달. 언마운트 시 cancelled=true.
   */
  cancelToken?: { cancelled: boolean };
}

export interface SubmitScoreResponse {
  accepted: boolean;
  serverVerifiedScore: number | null;
  rank?: number;
  isPersonalBest?: boolean;
  /** Phase 2 mock 임을 명시하는 플래그 */
  isMock: boolean;
  /**
   * Phase 3-C.1 결함 A 수정 — 서버 재계산 플래그.
   * - 'none': 공식 기록. 'low'/'high'/'macro'/'invalid': 공식 기록 아님.
   * - UI 는 flagged != 'none' 일 때 사용자에게 "공식 기록 아님" 안내 (사용자 지시 §A).
   * - Mock serverApi: 'none' 리턴.
   */
  flagged?: 'none' | 'low' | 'high' | 'macro' | 'invalid';
}

/**
 * 리더보드 초기화 요청 (system_admin 전용)
 *
 * Phase 3 BE 설계 (interface 만 Phase 2 에 선반영):
 *   - clientRequestId: 2단계 확인 UI 안에서 생성, 중복 실행 방지
 *   - reason: 초기화 사유 (필수, 20자 이상 권장)
 *   - 서버 처리:
 *       (1) acidRainMeta/leaderboard 문서 transaction read
 *       (2) epoch += 1, lastResetAt=now, lastResetByUid/Name, lastResetReason 기록
 *       (3) acidRainLeaderboard/{epoch} 집계만 새로 사용 → 과거 플레이 이력(acidRainSessions)은 보존
 *       (4) acidRainBestScores/{uid} 는 epoch 필드로 세대 구분. 조회 시 현재 epoch 만 반영
 *   - 동시성: 초기화 트랜잭션 커밋 전에 들어온 점수 저장은 과거 epoch 기록으로 남고, 커밋 후 저장 요청은 새 epoch 반영
 *   - 과거 이력 열람은 별도 "지난 시즌" API 로 Phase 3 확장 가능
 */
export interface ResetLeaderboardRequest {
  clientRequestId: string;
  reason: string;
}

export interface ResetLeaderboardResponse {
  success: boolean;
  epoch: number;
  resetAt: number;
  resetByName: string | null;
  reason: string;
  isMock: boolean;
}

export interface LeaderboardMeta {
  epoch: number;
  lastResetAt: number | null;
  lastResetByName: string | null;
  lastResetReason: string | null;
}

/**
 * 코인 정책 조회/변경 (system_admin 전용)
 *
 * Phase 3 BE 설계:
 *   - 설정 문서: `acidRainMeta/coinPolicy` 단일 문서
 *   - 변경 시 Firestore transaction: previous → next 변경 + `acidRainCoinPolicyHistory/{autoId}` append
 *   - 변경 소급 적용 금지: 진행 중 세션은 세션 생성 시점의 policy snapshot 사용 (startSession 응답에 policy 포함, 서버 트랜잭션에서 결정)
 *   - unlimited → limited 전환: 당일 사용 이력 보존, 새 세션부터 limit 적용
 *   - limited → unlimited 전환: 당일 사용 이력 보존, 새 세션부터 차감 skip
 *   - clientRequestId: 중복 저장 방지
 *   - 관리자 권한은 서버에서도 재검증 (callable 안에서 checkIsSystemAdmin)
 */
export interface UpdateCoinPolicyRequest {
  clientRequestId: string;
  policy: CoinPolicy;
}

export interface UpdateCoinPolicyResponse {
  success: boolean;
  previous: CoinPolicy;
  next: CoinPolicy;
  changedAt: number;
  isMock: boolean;
}

export interface AcidRainServerApi {
  getSettings: () => Promise<AcidRainSettings>;
  getDailyCoinStatus: () => Promise<DailyCoinStatus>;
  startSession: (req: StartSessionRequest) => Promise<StartSessionResponse>;
  submitScore: (req: SubmitScoreRequest) => Promise<SubmitScoreResponse>;
  getLeaderboard: () => Promise<LeaderboardRow[]>;
  /**
   * 특정 사용자의 현재 epoch 기준 최고점.
   * Phase 2: uid 를 FE 가 전달 (useCurrentUser)
   * Phase 3: uid 미전달 시 토큰 uid 사용 (서버 신뢰). 호환 shape 유지.
   */
  getMyBestScore: (uid: string) => Promise<{ bestScore: number | null; rank: number | null; epoch: number }>;
  getLeaderboardMeta: () => Promise<LeaderboardMeta>;
  /** system_admin 전용. Phase 2 는 mock */
  resetLeaderboard: (req: ResetLeaderboardRequest) => Promise<ResetLeaderboardResponse>;
  /** 공개 — 모든 플레이어가 조회 가능 (현재 정책 상태 UI 표시 용) */
  getCoinPolicy: () => Promise<CoinPolicy>;
  /** system_admin 전용. Phase 2 는 mock */
  updateCoinPolicy: (req: UpdateCoinPolicyRequest) => Promise<UpdateCoinPolicyResponse>;
  /** system_admin 전용. 최근 변경 이력 (최대 limit 건, 최신순) */
  getCoinPolicyHistory: (opts?: { limit?: number }) => Promise<CoinPolicyChange[]>;
  /**
   * V70 오픈 기간 설정 저장 (system_admin 전용).
   * Phase 2: mock localStorage. Phase 3: Firestore acidRainSettings 업데이트 + history.
   */
  updateOpenPeriod: (req: { openStartAt: number | null; openEndAt: number | null }) => Promise<{ success: boolean; openStartAt: number | null; openEndAt: number | null; isMock: boolean }>;
}

/**
 * Phase 2 Mock 구현
 *
 * 저장 범위:
 *   - 코인 정책 / 당일 사용량 / 리더보드 메타 / 개인 최고점 → localStorage
 *     (새로고침 후 설정·사용 이력 유지. KST 자정 리셋만 ymd 매칭 로직으로 처리)
 *   - 다른 브라우저/다른 세션/다른 사용자와는 공유되지 않음 (mock 특성)
 *   - Phase 3 에서 Firebase callable + Firestore 로 교체
 */
const MOCK_COIN_USED_KEY = 'acidRain.mock.dailyCoinUsed';
const MOCK_COIN_DATE_KEY = 'acidRain.mock.dailyCoinDate';
const MOCK_LB_META_KEY = 'acidRain.mock.lbMeta';
const MOCK_COIN_POLICY_KEY = 'acidRain.mock.coinPolicy';
const MOCK_OPEN_PERIOD_KEY = 'acidRain.mock.openPeriod';  // V70 오픈 기간
const MOCK_COIN_POLICY_HIST_KEY = 'acidRain.mock.coinPolicyHistory';
/**
 * 사용자 점수 저장 (mock 리더보드 데이터).
 * 저장 구조: `MockUserEntry[]` (JSON array)
 * 세대(epoch) 필드로 리셋 전/후 자동 분리. resetLeaderboard 는 entries 를
 * 지우지 않고 epoch 만 올리므로, 과거 세대 데이터는 보존되나 조회에서
 * current epoch 만 반영 → 이전 epoch 점수가 새 리더보드에 재등장하지 않음.
 */
const MOCK_USER_ENTRIES_KEY = 'acidRain.mock.userEntries';

interface MockUserEntry {
  uid: string;
  displayName: string;
  companyCode?: 'KR' | 'CN';
  bestScore: number;
  recordedAt: number;
  /** 세대 — getLeaderboard/getMyBestScore 조회 필터. */
  epoch: number;
  /** V70: 저장 시점 소속 조직 ID. 조회 시 조직명 매핑. */
  orgUnitId?: string;
}

function safeGetLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore (private mode etc.) */
  }
}

function safeRemoveLocal(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function getKstYmd(): string {
  const now = new Date();
  const kstMs = now.getTime() + 9 * 60 * 60 * 1000;
  const d = new Date(kstMs);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function readMockDailyUsed(): number {
  const savedDate = safeGetLocal(MOCK_COIN_DATE_KEY);
  const today = getKstYmd();
  if (savedDate !== today) {
    // KST 자정 지나면 자동 리셋 (정책 모드와 무관)
    safeSetLocal(MOCK_COIN_DATE_KEY, today);
    safeSetLocal(MOCK_COIN_USED_KEY, '0');
    return 0;
  }
  return Number(safeGetLocal(MOCK_COIN_USED_KEY) || '0');
}

function writeMockDailyUsed(used: number): void {
  safeSetLocal(MOCK_COIN_USED_KEY, String(used));
  safeSetLocal(MOCK_COIN_DATE_KEY, getKstYmd());
}

function readMockCoinPolicy(): CoinPolicy {
  const raw = safeGetLocal(MOCK_COIN_POLICY_KEY);
  if (!raw) return { ...DEFAULT_COIN_POLICY };
  try {
    const p = JSON.parse(raw) as CoinPolicy;
    if (p.mode !== 'unlimited' && p.mode !== 'limited') return { ...DEFAULT_COIN_POLICY };
    const lim = Number.isFinite(p.dailyLimit) ? Math.max(1, Math.min(100, Math.floor(p.dailyLimit))) : DEFAULT_COIN_POLICY.dailyLimit;
    return { mode: p.mode, dailyLimit: lim };
  } catch {
    return { ...DEFAULT_COIN_POLICY };
  }
}

function writeMockCoinPolicy(p: CoinPolicy): void {
  safeSetLocal(MOCK_COIN_POLICY_KEY, JSON.stringify(p));
}

function readMockCoinPolicyHistory(): CoinPolicyChange[] {
  const raw = safeGetLocal(MOCK_COIN_POLICY_HIST_KEY);
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr as CoinPolicyChange[];
  } catch {
    return [];
  }
}

function appendMockCoinPolicyHistory(entry: CoinPolicyChange): void {
  const prev = readMockCoinPolicyHistory();
  const next = [entry, ...prev].slice(0, 20);
  safeSetLocal(MOCK_COIN_POLICY_HIST_KEY, JSON.stringify(next));
}

// V70-A — 가상 순위(SAMPLE_LEADERBOARD, demo-1~demo-10) 제거.
//   공식 리더보드는 Firestore 에 저장된 유효 기록만 표시한다 (서버 모드 getPublicTournamentLeaderboard).
//   mock(브라우저 저장) 모드에는 공식 기록 조회 경로가 없으므로 공식 순위는 항상 빈 목록이며,
//   이 브라우저의 localStorage 기록은 본인 최고점 표시에만 쓰고 순위로 노출하지 않는다.

function readMockMeta(): LeaderboardMeta {
  const raw = safeGetLocal(MOCK_LB_META_KEY);
  if (!raw) return { epoch: 1, lastResetAt: null, lastResetByName: null, lastResetReason: null };
  try {
    return JSON.parse(raw) as LeaderboardMeta;
  } catch {
    return { epoch: 1, lastResetAt: null, lastResetByName: null, lastResetReason: null };
  }
}

function writeMockMeta(meta: LeaderboardMeta): void {
  safeSetLocal(MOCK_LB_META_KEY, JSON.stringify(meta));
}

function readUserEntries(): MockUserEntry[] {
  const raw = safeGetLocal(MOCK_USER_ENTRIES_KEY);
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return (arr as MockUserEntry[]).filter(
      (e) =>
        e &&
        typeof e.uid === 'string' &&
        typeof e.bestScore === 'number' &&
        Number.isFinite(e.bestScore) &&
        typeof e.epoch === 'number'
    );
  } catch {
    return [];
  }
}

function writeUserEntries(entries: MockUserEntry[]): void {
  safeSetLocal(MOCK_USER_ENTRIES_KEY, JSON.stringify(entries));
}

/**
 * 동일 사용자 + 동일 epoch 는 1건만 유지, 신규 >= 기존일 때만 교체 (`>` 이면 더 엄격).
 * 사용자 지시: "기존 최고점보다 높은 점수일 때만 갱신" → strict `>` 비교.
 *
 * 과거 epoch 의 같은 사용자 기록은 보존 (리셋 전 통계 열람 가능).
 */
function upsertUserBestScore(entry: MockUserEntry): { stored: boolean; prevBest: number | null } {
  const list = readUserEntries();
  let prevBest: number | null = null;
  let found = false;
  const next: MockUserEntry[] = [];
  for (const e of list) {
    if (e.uid === entry.uid && e.epoch === entry.epoch) {
      found = true;
      prevBest = e.bestScore;
      if (entry.bestScore > e.bestScore) {
        next.push({ ...e, ...entry });
      } else {
        next.push(e);
      }
    } else {
      next.push(e);
    }
  }
  if (!found) next.push(entry);
  writeUserEntries(next);
  const stored = !found || entry.bestScore > (prevBest ?? -Infinity);
  return { stored, prevBest };
}

/**
 * V70-A — mock 모드의 공식 순위: 항상 빈 목록.
 *   가상 데이터로 채우지 않고, localStorage 기록도 전사 공식 순위처럼 노출하지 않는다.
 */
function getMockLeaderboard(): LeaderboardRow[] {
  return [];
}

/**
 * V70-A — 게임대회 전체 공개 여부 (서버 getPublicTournamentGameSettings.enabled = tournamentSettings.enabled).
 *   조회 실패·미조회 시 비공개(false) — fail-closed. 테스트 환경은 기존 mock 전제(공개) 유지.
 */
let acidServerEnabled = false;

function mockSleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function readMockOpenPeriod(): { openStartAt: number | null; openEndAt: number | null } {
  const raw = safeGetLocal(MOCK_OPEN_PERIOD_KEY);
  if (!raw) return { openStartAt: null, openEndAt: null };
  try {
    const p = JSON.parse(raw);
    return {
      openStartAt: typeof p.openStartAt === 'number' ? p.openStartAt : null,
      openEndAt: typeof p.openEndAt === 'number' ? p.openEndAt : null,
    };
  } catch {
    return { openStartAt: null, openEndAt: null };
  }
}

function writeMockOpenPeriod(v: { openStartAt: number | null; openEndAt: number | null }): void {
  safeSetLocal(MOCK_OPEN_PERIOD_KEY, JSON.stringify(v));
}

// ─── Firestore 공통 설정 → 로컬 캐시 동기화 (사용자 지시: 어드민 저장값이 실제 적용) ───

/**
 * Firestore SSoT 로부터 acidRain 공개 설정을 가져와 로컬 캐시(코인정책/오픈기간/epoch)에 반영.
 * 테스트 환경에서는 no-op. 실패 시 getter 는 fail-open — 실제 플레이는 startSession 안에서
 * assertTournamentGameAccess 가 다시 BE 재검증하므로 fail-closed 보장됨 (사용자 지시 §3).
 */
async function syncAcidRainFromFirestore(): Promise<void> {
  if (process.env.NODE_ENV === 'test') { acidServerEnabled = true; return; }
  try {
    const pub = await callGetPublicTournamentGameSettings('acidRain');
    // V70-A — 마블과 동일하게 게임대회 전체 enabled 반영 (OFF → 비공개)
    acidServerEnabled = pub.enabled === true;
    writeMockOpenPeriod({
      openStartAt: pub.openStartAt ? Date.parse(pub.openStartAt) : null,
      openEndAt: pub.openEndAt ? Date.parse(pub.openEndAt) : null,
    });
    if (pub.coinPolicy && (pub.coinPolicy.mode === 'limited' || pub.coinPolicy.mode === 'unlimited')) {
      const n = Math.max(1, Math.min(100, Math.floor(Number(pub.coinPolicy.dailyLimit) || DEFAULT_COIN_POLICY.dailyLimit)));
      writeMockCoinPolicy({ mode: pub.coinPolicy.mode, dailyLimit: n });
    }
    if (Number.isFinite(pub.leaderboardEpoch) && pub.leaderboardEpoch > 0) {
      const meta = readMockMeta();
      if (meta.epoch !== pub.leaderboardEpoch) {
        writeMockMeta({
          epoch: pub.leaderboardEpoch,
          lastResetAt: pub.lastResetAt ? Date.parse(pub.lastResetAt) : meta.lastResetAt,
          lastResetByName: meta.lastResetByName,
          lastResetReason: pub.lastResetReason || meta.lastResetReason,
        });
      }
    }
  } catch {
    // V70-A — 설정 조회 실패 시 공개로 두지 않음 (fail-closed). 기간·정책 캐시는 기존 값 유지.
    acidServerEnabled = false;
  }
}

/**
 * startSession 안에서 호출 — access 응답으로 로컬 캐시 즉시 갱신.
 */
function applyAccessRespToLocalCache(access: AccessCheckResp): void {
  if (process.env.NODE_ENV === 'test') return;
  try {
    writeMockOpenPeriod({
      openStartAt: access.openStartAt ? Date.parse(access.openStartAt) : null,
      openEndAt: access.openEndAt ? Date.parse(access.openEndAt) : null,
    });
    if (access.policy && (access.policy.mode === 'limited' || access.policy.mode === 'unlimited')) {
      const n = Math.max(1, Math.min(100, Math.floor(Number(access.policy.dailyLimit) || DEFAULT_COIN_POLICY.dailyLimit)));
      writeMockCoinPolicy({ mode: access.policy.mode, dailyLimit: n });
    }
    if (access.leaderboardEpoch != null && Number.isFinite(access.leaderboardEpoch) && access.leaderboardEpoch > 0) {
      const meta = readMockMeta();
      if (meta.epoch !== access.leaderboardEpoch) {
        writeMockMeta({ ...meta, epoch: access.leaderboardEpoch });
      }
    }
  } catch {
    // cache 쓰기 실패는 플레이 자체를 막지 않음 (BE 재검증 통과).
  }
}

export const mockServerApi: AcidRainServerApi = {
  async getSettings() {
    await syncAcidRainFromFirestore();
    await mockSleep(50);
    const period = readMockOpenPeriod();
    return { ...DEFAULT_SETTINGS, enabled: acidServerEnabled, openStartAt: period.openStartAt, openEndAt: period.openEndAt };
  },

  async getDailyCoinStatus() {
    await syncAcidRainFromFirestore();
    await mockSleep(50);
    const policy = readMockCoinPolicy();
    const used = readMockDailyUsed();
    if (policy.mode === 'unlimited') {
      return {
        ymd: getKstYmd(),
        mode: 'unlimited',
        used,
        limit: null,
        remaining: null,
      };
    }
    return {
      ymd: getKstYmd(),
      mode: 'limited',
      used,
      limit: policy.dailyLimit,
      remaining: Math.max(0, policy.dailyLimit - used),
    };
  },

  async startSession(req) {
    // V70 Phase 3-A §1: 매 세션 시작 전 BE 재검증 (Firestore 공통 설정 기반). fail-closed.
    // 응답으로 로컬 캐시 즉시 갱신 → Firestore SSoT 가 실제 세션 생성에 반영됨.
    const access = await assertTournamentGameAccess('acidRain');
    applyAccessRespToLocalCache(access);
    await mockSleep(100);
    // V70 오픈 기간 체크 (하위 호환: 미설정이면 skip) — BE 통과 후 로컬 재검증
    const period = readMockOpenPeriod();
    const now = Date.now();
    if (period.openStartAt != null && now < period.openStartAt) {
      throw new Error('아직 대회가 시작되지 않았습니다. (COMING SOON)');
    }
    if (period.openEndAt != null && now >= period.openEndAt) {
      throw new Error('대회가 종료되었습니다.');
    }
    const policy = readMockCoinPolicy();
    const used = readMockDailyUsed();
    // 세션 생성 시점의 policy/epoch 스냅샷 사용 (사용자 지시: 소급 적용 금지)
    if (policy.mode === 'limited') {
      if (used >= policy.dailyLimit) {
        throw new Error('오늘 도전 가능 횟수를 모두 사용했습니다. 내일 00:00 KST 에 다시 도전 가능합니다.');
      }
      writeMockDailyUsed(used + 1);
    } else {
      // unlimited: 사용 이력은 참고용으로만 증가 (리더보드 영향 없음, 당일 total 통계용)
      writeMockDailyUsed(used + 1);
    }
    const meta = readMockMeta();
    const sessionId = `mock-session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const seed = `${sessionId}:${req.clientRequestId}`;
    return {
      sessionId,
      seed,
      commitmentHash: `mock-commitment-${sessionId}`,
      settings: { ...DEFAULT_SETTINGS, enabled: true },
      startedAtServerMs: Date.now(),
      leaderboardEpoch: meta.epoch,
    };
  },

  async submitScore(req) {
    await mockSleep(80);
    // 사용자 식별 (mock): request.user 신뢰
    if (!req.user || !req.user.uid) {
      throw new Error('사용자 정보가 누락되었습니다. (Phase 3 에서는 서버 토큰 uid 사용)');
    }
    if (!req.sessionEpoch || !Number.isFinite(req.sessionEpoch)) {
      throw new Error('sessionEpoch 가 누락되었습니다.');
    }
    const score = req.result.clientReportedScore;
    if (!Number.isFinite(score) || score < 0) {
      throw new Error('잘못된 점수 값입니다.');
    }
    // 세대 분리: 저장은 session 생성 시점의 epoch (sessionEpoch) 로.
    // current epoch 와 다르면 getLeaderboard 에서 자동으로 제외됨 → 과거 세션 점수가
    // 새 리더보드에 유입되지 않음.
    const { stored, prevBest } = upsertUserBestScore({
      uid: req.user.uid,
      // ISSUE-01: 하드코딩 fallback 제거 — displayName 이 비어 있으면 그대로 저장.
      //           리더보드 UI 에서 조회 시점에 fallback 처리 (사용자 지시 §"임의 생성 금지").
      displayName: req.user.displayName || '',
      companyCode: req.user.companyCode,
      bestScore: score,
      recordedAt: Date.now(),
      epoch: req.sessionEpoch,
      orgUnitId: req.user.orgUnitId,
    });
    // current epoch 안에서 rank 계산 (저장된 경우에만 의미 있음)
    const curMeta = readMockMeta();
    let rank: number | undefined;
    let isPersonalBest = stored && (prevBest == null || score > prevBest);
    if (req.sessionEpoch === curMeta.epoch) {
      const lb = getMockLeaderboard();
      const idx = lb.findIndex((r) => r.uid === req.user.uid);
      rank = idx >= 0 ? lb[idx].rank : undefined;
    } else {
      // 세션 생성 후 리셋되어 current epoch 가 바뀜 → 리더보드에 노출 안 됨.
      // 저장은 성공했지만 공식 랭킹에 들어가지 않는 상태 (사용자 지시 준수).
      rank = undefined;
      isPersonalBest = false;
    }
    return {
      accepted: true,
      serverVerifiedScore: null,
      rank,
      isPersonalBest,
      isMock: true,
    };
  },

  async getLeaderboard() {
    await syncAcidRainFromFirestore();
    await mockSleep(100);
    return getMockLeaderboard();
  },

  async getMyBestScore(uid) {
    await syncAcidRainFromFirestore();
    await mockSleep(50);
    const meta = readMockMeta();
    if (!uid) return { bestScore: null, rank: null, epoch: meta.epoch };
    const entries = readUserEntries().filter((e) => e.epoch === meta.epoch && e.uid === uid);
    if (entries.length === 0) return { bestScore: null, rank: null, epoch: meta.epoch };
    const best = entries.reduce((m, e) => (e.bestScore > m ? e.bestScore : m), -Infinity);
    const lb = getMockLeaderboard();
    const idx = lb.findIndex((r) => r.uid === uid);
    return { bestScore: best, rank: idx >= 0 ? lb[idx].rank : null, epoch: meta.epoch };
  },

  async getLeaderboardMeta() {
    await syncAcidRainFromFirestore();
    await mockSleep(30);
    return readMockMeta();
  },

  async resetLeaderboard(req) {
    const trimmed = (req.reason || '').trim();
    if (trimmed.length < 5) throw new Error('초기화 사유를 5자 이상 입력해주세요.');
    if (!req.clientRequestId) throw new Error('clientRequestId 누락.');
    // V70 Phase 3-A §3: Firestore 전역 저장 (SSoT). 로컬 cache 는 fallback.
    await callResetTournamentLeaderboard('acidRain', trimmed);
    await mockSleep(120);
    const cur = readMockMeta();
    const next: LeaderboardMeta = {
      epoch: cur.epoch + 1,
      lastResetAt: Date.now(),
      lastResetByName: '현재 관리자 (mock)',
      lastResetReason: trimmed,
    };
    writeMockMeta(next);
    // user entries 는 **지우지 않음** — epoch 세대 구분으로 자동 분리.
    // 과거 세대 데이터는 보존, 조회는 current epoch 만 반영.
    return {
      success: true,
      epoch: next.epoch,
      resetAt: next.lastResetAt!,
      resetByName: next.lastResetByName,
      reason: trimmed,
      isMock: true,
    };
  },

  async getCoinPolicy() {
    await syncAcidRainFromFirestore();
    await mockSleep(30);
    return readMockCoinPolicy();
  },

  async updateCoinPolicy(req) {
    await mockSleep(80);
    if (!req.clientRequestId) throw new Error('clientRequestId 누락.');
    const { policy } = req;
    if (policy.mode !== 'unlimited' && policy.mode !== 'limited') {
      throw new Error('mode 는 unlimited 또는 limited 이어야 합니다.');
    }
    if (policy.mode === 'limited') {
      if (!Number.isFinite(policy.dailyLimit)) throw new Error('dailyLimit 은 정수여야 합니다.');
      const n = Math.floor(policy.dailyLimit);
      if (n < 1 || n > 100) throw new Error('dailyLimit 은 1~100 사이여야 합니다.');
    }
    const prev = readMockCoinPolicy();
    const next: CoinPolicy = {
      mode: policy.mode,
      // unlimited 로 전환 시 dailyLimit 은 다음 limited 복귀 시 복원되는 값 — 기본값으로 보존
      dailyLimit:
        policy.mode === 'limited'
          ? Math.floor(policy.dailyLimit)
          : prev.dailyLimit, // 모드만 전환했을 때 과거 limited 값 유지
    };
    // V70 Phase 3-A §3: Firestore 전역 저장 (SSoT). 로컬 cache 는 fallback.
    await callUpdateTournamentCoinPolicy({ mode: next.mode, dailyLimit: next.dailyLimit });
    // 중요: 당일 사용 이력(used)은 건드리지 않음 — 모드 전환/limit 변경 시에도 보존
    writeMockCoinPolicy(next);
    const change: CoinPolicyChange = {
      id: `chg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      changedAt: Date.now(),
      changedByName: '현재 관리자 (mock)',
      previous: prev,
      next,
    };
    appendMockCoinPolicyHistory(change);
    return {
      success: true,
      previous: prev,
      next,
      changedAt: change.changedAt,
      isMock: true,
    };
  },

  async getCoinPolicyHistory(opts) {
    await mockSleep(40);
    const limit = Math.max(1, Math.min(20, opts?.limit ?? 10));
    return readMockCoinPolicyHistory().slice(0, limit);
  },

  async updateOpenPeriod(req) {
    const start = req.openStartAt;
    const end = req.openEndAt;
    if (start != null && end != null && start >= end) {
      throw new Error('종료 일시는 시작 일시보다 뒤여야 합니다.');
    }
    // V70 Phase 3-A §3: Firestore 전역 저장 (SSoT). 로컬 cache 는 fallback.
    await callUpdateTournamentOpenPeriod(
      'acidRain',
      start != null ? new Date(start).toISOString() : null,
      end != null ? new Date(end).toISOString() : null,
    );
    await mockSleep(40);
    writeMockOpenPeriod({ openStartAt: start, openEndAt: end });
    return { success: true, openStartAt: start, openEndAt: end, isMock: true };
  },
};

/**
 * V70 Phase 3-C: Feature flag 분기 — QA + env flag 둘 다 ON 이면 서버 serverApi 사용.
 * OFF 상태에서는 기존 mockServerApi 유지 (운영 보호). import 는 파일 상단으로 이동 (ESLint import/first).
 */
export const acidRainApi: AcidRainServerApi = isPhase3cServerEnabled() ? serverAcidRainApi : mockServerApi;
